import * as React from 'react'

import { DefaultContext } from '@apollo/client'
import type { DataTableFilter, DataTableSource } from 'generated/resources/datatable.graphql'

import {
  DEFAULT_ITEMS_PER_PAGE,
  PAGINATION_KIND,
  SORT_DIRECTION,
} from 'modules/core/components/DataTable/constants'
import { DataProvider } from 'modules/core/components/DataTable/contexts/DataContext'
import { FiltersProvider } from 'modules/core/components/DataTable/contexts/FiltersContext'
import { PaginationStateProvider } from 'modules/core/components/DataTable/contexts/PaginationStateContext'
import { ProjectionsProvider } from 'modules/core/components/DataTable/contexts/ProjectionsContext'
import SelectedRowsProvider from 'modules/core/components/DataTable/contexts/SelectedRowsContext'
import { SortProvider } from 'modules/core/components/DataTable/contexts/SortContext'
import type { PaginationType, SortType } from 'modules/core/components/DataTable/types'

const DEFAULT_PAGINATION: PaginationType = {
  kind: PAGINATION_KIND.OFFSET,
  itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
}

type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>

export type ProviderProps<Paths, Filters, SortFields extends string> = {
  persistFilters?: boolean
  enableClearFilters?: boolean
  queryContext?: DefaultContext
  source: DataTableSource<Paths, SortFields>
  pagination?: PartialBy<PaginationType, 'itemsPerPage'>
  sort?: PartialBy<SortType<SortFields>, 'direction'>
  onSortCallback?: (sort: SortType<SortFields>) => void
  filters?: Array<DataTableFilter<Filters>>
  rowTo?: (args: Pick<Paths, keyof Paths>) => string | undefined
  children?: React.ReactNode
}

export default function Provider<Paths, Filters, SortFields extends string>({
  source,
  pagination: paginationProp,
  sort: sortProp,
  filters,
  rowTo,
  children,
  persistFilters = true,
  enableClearFilters = false,
  queryContext,
  onSortCallback,
}: ProviderProps<Paths, Filters, SortFields>) {
  const pagination = {
    ...DEFAULT_PAGINATION,
    ...paginationProp,
  }

  const sort = {
    path: source.sort.default,
    direction: SORT_DIRECTION.ASC,
    ...sortProp,
  }

  return (
    <ProjectionsProvider source={source}>
      <PaginationStateProvider pagination={pagination}>
        <SortProvider source={source} sort={sort} onSortCallback={onSortCallback}>
          <FiltersProvider
            source={source}
            filters={filters}
            persistFilters={persistFilters}
            enableClearFilters={enableClearFilters}
          >
            <DataProvider source={source} rowTo={rowTo} queryContext={queryContext}>
              <SelectedRowsProvider>{children}</SelectedRowsProvider>
            </DataProvider>
          </FiltersProvider>
        </SortProvider>
      </PaginationStateProvider>
    </ProjectionsProvider>
  )
}
